import { CaseParameters } from "../shared/CaseModel";
import {
  CASE_EXPORT_APP,
  CASE_EXPORT_TYPE,
  CASE_EXPORT_VERSION,
  CaseExportFile,
  isCaseExportFile,
} from "../shared/caseExport";

const PBKDF2_ITERATIONS = 200_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

/**
 * Thrown when an export file cannot be decrypted — either the password is wrong
 * or the file is corrupt/not a casesolv export. The two are deliberately not
 * distinguished (AES-GCM auth failure looks identical to tampering).
 */
export class CaseDecryptionError extends Error {
  constructor(message = "Failed to decrypt case export") {
    super(message);
    this.name = "CaseDecryptionError";
  }
}

/**
 * Thrown when an export file declares a format version this client cannot
 * read (i.e. it was produced by a newer build). Kept distinct from
 * {@link CaseDecryptionError} so the UI can tell the user to upgrade rather
 * than blaming their password.
 */
export class CaseVersionError extends Error {
  readonly fileVersion: unknown;
  constructor(fileVersion: unknown) {
    super(`Unsupported case export version ${String(fileVersion)}`);
    this.name = "CaseVersionError";
    this.fileVersion = fileVersion;
  }
}

function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function deriveKey(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Encrypt a case's parameters into a downloadable export envelope. */
export async function encryptConfig(
  config: CaseParameters,
  password: string,
): Promise<CaseExportFile> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(password, salt, PBKDF2_ITERATIONS);

  const plaintext = new TextEncoder().encode(JSON.stringify(config));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext,
  );

  return {
    app: CASE_EXPORT_APP,
    type: CASE_EXPORT_TYPE,
    version: CASE_EXPORT_VERSION,
    kdf: {
      name: "PBKDF2",
      salt: toBase64(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    cipher: { name: "AES-GCM", iv: toBase64(iv) },
    data: toBase64(ciphertext),
  };
}

/**
 * Decrypt an export envelope back into a case's parameters. Throws
 * {@link CaseDecryptionError} on a wrong password or a malformed/foreign file.
 */
export async function decryptConfig(
  file: unknown,
  password: string,
): Promise<CaseParameters> {
  if (!isCaseExportFile(file)) {
    throw new CaseDecryptionError("Not a valid case export file");
  }

  // Reject files from a newer format than we understand. Older versions stay
  // readable (none exist yet); only a higher version is unsupported. Checked
  // before decryption so it surfaces a clear "upgrade" message, not a password
  // error. Must stay outside the try below, which collapses everything into a
  // CaseDecryptionError.
  if (typeof file.version !== "number" || file.version > CASE_EXPORT_VERSION) {
    throw new CaseVersionError(file.version);
  }

  try {
    const salt = fromBase64(file.kdf.salt);
    const iv = fromBase64(file.cipher.iv);
    const key = await deriveKey(password, salt, file.kdf.iterations);
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      fromBase64(file.data),
    );
    const config = JSON.parse(new TextDecoder().decode(plaintext));
    if (typeof config !== "object" || config === null || !config.name) {
      throw new CaseDecryptionError("Decrypted data is not a case");
    }
    return config as CaseParameters;
  } catch (error) {
    if (error instanceof CaseDecryptionError) throw error;
    // AES-GCM auth failure (wrong password / tampering) or bad base64/JSON
    throw new CaseDecryptionError();
  }
}
